'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/Components/ui/badge'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/Components/ui/input-otp'
import { Label } from '@/Components/ui/label'
import { ArrowLeft, X, ArrowRight, ChevronRight } from 'lucide-react'
import WordFadeIn from '@/Components/ui/word-fade-in'
import { adjustFont, cn, formatTime, LocalizedText } from '@/lib/utils'
import { LazySvg } from '@/Components/custom/lazy-svg'
import PulsatingButton from '@/Components/ui/pulsating-button'
import { useToast } from '@/hooks/use-toast'
import { LanguageCode, Translatable } from '@/types'
import useLocalizedValue, { useLocalizedBundle } from '@/hooks/use-localized-value'
import { Link, useForm, usePage } from '@inertiajs/react'
import { Bundle } from '@/types'
import { BundleDetailLineItemPlus, BundleRateDetailLineItem } from './bundle/detail-list'
import { useTranslation } from 'react-i18next'
import { useSafeDirection } from '@/hooks/use-safe-translation'
import { BorderBeam } from '@/Components/magicui/border-beam'
export interface TelecomPlansGrid1Props {
  bundles: Bundle[]
  title?: Translatable<string> | null
  subtitle?: Translatable<string> | null
  className?: string
}

const imageVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.2 },
}

const FormStage = {
  INITIAL: 'initial',
  PHONE: 'phone',
  PIN: 'pin',
} as const

type FormStageType = (typeof FormStage)[keyof typeof FormStage]
interface DynamicIslandProps {
  bundle: Bundle
  isExpanded: boolean
  onClick: () => void
  onClose: () => void
}

const DynamicIsland: React.FC<DynamicIslandProps> = ({ bundle, isExpanded, onClick, onClose }) => {
  const { toast } = useToast()
  const [isHovered, setIsHovered] = useState(false)
  const { t, i18n } = useTranslation()
  const { component, props } = usePage()

  const localizedBundle = useLocalizedBundle(bundle)

  const [titleFontSize, setTitleFontSize] = useState('2.25rem')
  const isTezNet = component === 'TezNet'
  const isBundle = !!bundle.category && bundle.category === 'none_alpha'
  const isMonetized = !!bundle.monetization && bundle.monetization === 'no'

  const [resendTimer, setResendTimer] = useState(120)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const otpRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const [canResend, setCanResend] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [rateLimitTimer, setRateLimitTimer] = useState(0)
  const rateLimitTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [hideInlineError, setHideInlineError] = useState(false)

  const phoneForm = useForm({
    msisdn: '',
    locale: i18n.language,
    bundle: typeof bundle.id === 'string' || typeof bundle.id === 'number' ? bundle.id : '',
  })

  const otpForm = useForm({
    msisdn: '',
    pin: '',
    locale: i18n.language,
    bundle: typeof bundle.id === 'string' || typeof bundle.id === 'number' ? bundle.id : '',
  })

  const [stage, setStage] = useState<FormStageType>(FormStage.INITIAL)

  const isValidPhone = phoneForm.data.msisdn
    ? /^07[29]\d{7}$/.test(`${phoneForm.data.msisdn}`)
    : false
  const canProceedToPin = String(phoneForm.data.msisdn).length === 10 && isValidPhone
  const canActivate = typeof otpForm.data.pin === 'string' && otpForm.data.pin.length === 6
  const phoneError = phoneForm.errors.msisdn || phoneForm.errors.bundle
  const otpError = otpForm.errors.pin || otpForm.errors.msisdn || otpForm.errors.bundle

  useEffect(() => {
    if (isHovered && !isExpanded) {
      setStage(FormStage.INITIAL)
      otpForm.setDefaults()
      phoneForm.setDefaults()
      phoneForm.setData('locale', i18n.language)
      otpForm.setData('locale', i18n.language)
    }
  }, [isHovered, isExpanded])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isExpanded) {
        handleClose()
      }
    }

    if (isExpanded) {
      window.addEventListener('keydown', onKeyDown)
    }

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isExpanded])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded])

  useEffect(() => {
    if (stage === FormStage.PIN) {
      setResendTimer(120)
      setCanResend(false)
    }
    if (stage === FormStage.PHONE && phoneRef.current) {
      phoneRef.current.focus()
    }
    if (stage === FormStage.PIN && otpRef.current) {
      otpRef.current.focus()
    }
  }, [stage])

  useEffect(() => {
    if (stage === FormStage.PIN && !canResend) {
      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [stage, canResend])

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitTimer > 0) {
      rateLimitTimerRef.current = setInterval(() => {
        setRateLimitTimer((prev) => {
          if (prev <= 1) {
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => {
      if (rateLimitTimerRef.current) {
        clearInterval(rateLimitTimerRef.current)
      }
    }
  }, [rateLimitTimer > 0])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (stage === FormStage.PHONE && canProceedToPin && !phoneForm.processing) {
          handleNext()
          e.preventDefault()
        } else if (stage === FormStage.PIN && canActivate && !otpForm.processing) {
          handleActivate()
          e.preventDefault()
        } else if (stage === FormStage.INITIAL && !phoneForm.processing) {
          handleNext()
          e.preventDefault()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [stage, canProceedToPin, canActivate, phoneForm.processing, otpForm.processing])

  // Auto-activate when OTP is complete
  useEffect(() => {
    if (stage === FormStage.PIN && canActivate && !otpForm.processing && !otpForm.hasErrors) {
      // Add a small delay to ensure the UI updates before activation
      const timer = setTimeout(() => {
        handleActivate()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [stage, canActivate, otpForm.processing])

  const handleNext = () => {
    if (stage === FormStage.INITIAL) {
      setStage(FormStage.PHONE)
      setResendTimer(120)
      setCanResend(false)
    } else if (stage === FormStage.PHONE && canProceedToPin) {
      // Submit phone number to backend to send OTP
      phoneForm.post('/bundles/verify', {
        onSuccess: () => {
          // Copy phone number to OTP form and proceed to PIN stage
          otpForm.setData('msisdn', phoneForm.data.msisdn)
          setStage(FormStage.PIN)
          setResendTimer(120)
          setCanResend(false)
          phoneForm.clearErrors()
          setRateLimitTimer(0)
        },
        onError: (errors) => {
          // Check for rate limit error with retryAfter
          const retryAfter = errors.retryAfter as string | undefined
          if (retryAfter) {
            setRateLimitTimer(parseInt(retryAfter, 10))
          }
          setPhoneTouched(true)
          setTimeout(() => {
            phoneRef.current?.focus()
          }, 100)
        },
        preserveScroll: true,
      })
    }
  }

  const handleBack = () => {
    if (stage === FormStage.PIN) {
      setStage(FormStage.PHONE)
      setResendTimer(120)
      setCanResend(false)
      otpForm.setData('pin', '')
      otpForm.setData('msisdn', '')
      otpForm.clearErrors()
      setTimeout(() => {
        phoneRef.current?.focus()
      }, 100)
    } else if (stage === FormStage.PHONE) {
      setStage(FormStage.INITIAL)
      phoneForm.setData('msisdn', '')
      phoneForm.clearErrors()
    }
  }

  const handleActivate = () => {
    if (!canActivate) return

    otpForm.post('/bundles/purchase', {
      onSuccess: (page) => {
        const flash = page.props.flash as { status?: boolean; title?: string; message?: string }

        // If flash data exists, subscription was attempted - show toast and close
        // If no flash data, validation failed (OTP expired) - keep modal open for retry
        if (flash?.title && flash?.message) {
          toast({
            title: flash.title,
            description: flash.message,
            variant: flash.status ? 'success' : 'destructive',
          })
          handleClose()
        }
      },
      onError: (errors) => {
        // Check for rate limit error with retryAfter (should be rare with separate limiters)
        const retryAfter = errors.retryAfter as string | undefined
        if (retryAfter) {
          setRateLimitTimer(parseInt(retryAfter, 10))
        }
        // Keep modal open for retry
        setTimeout(() => {
          otpRef.current?.focus()
        }, 100)
      },
      preserveScroll: true,
    })
  }

  const handleClose = () => {
    otpForm.setData('pin', '')
    otpForm.setData('msisdn', '')
    phoneForm.setData('msisdn', '')
    phoneForm.reset()
    otpForm.reset()
    setResendTimer(120)
    setCanResend(false)
    setHideInlineError(false)
    setRateLimitTimer(0)
    if (timerRef.current) {
      setStage(FormStage.INITIAL)
      clearInterval(timerRef.current)
    }
    if (rateLimitTimerRef.current) {
      clearInterval(rateLimitTimerRef.current)
    }
    setStage(FormStage.INITIAL)
    onClose()
  }

  const getLocalizedValue = (
    value: LanguageCode | string | Translatable<string> | null | undefined
  ): string | null => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'object') {
      const language = i18n.language as LanguageCode
      const localizedValue = value[language] || value['en']
      return localizedValue && localizedValue !== '' ? localizedValue : null
    }
    return null
  }

  useEffect(() => {
    if (localizedBundle.title) {
      setTitleFontSize(adjustFont(localizedBundle.title))
    }
  }, [localizedBundle.title])

  const getCountry = (): string | null => {
    const country = localizedBundle.attributes.find((attr) => attr.category === 'country')?.settings
      .icon
    if (!country) return null

    return country as string
  }

  const getRates = () => {
    if (isTezNet && !isBundle && localizedBundle.attributes.length === 0) {
      return null
    }

    const legendLabel = localizedBundle.attributes.find(
      (attr) => attr.category === 'rate_note'
    )?.value
    if (!legendLabel) return null
    const rates = localizedBundle.attributes.filter((attr) =>
      ['onnet_minutes_rate', 'offnet_minutes_rate', 'onnet_sms_rate', 'offnet_sms_rate'].includes(
        attr.category as string
      )
    )
    if (!rates) return null

    return (
      <fieldset className="rounded-md border border-white/20 p-2">
        <legend className="px-1 text-sm font-medium text-white rtl:font-semibold">
          {legendLabel as string}
        </legend>
        <ul className="flex flex-col items-start justify-center gap-y-1.5 text-white/85">
          {rates.map((rate, index) => (
            <BundleRateDetailLineItem
              key={index}
              field={rate.value as Translatable<string>}
              label={rate.settings?.name}
              icon={rate.settings.icon}
            />
          ))}
        </ul>
      </fieldset>
    )
  }

  const getRegions = () => {
    if (isTezNet && !isBundle && localizedBundle.attributes.length === 0) {
      return null
    }
    const regionsAttr = localizedBundle.attributes.find((attr) => attr.category === 'regions')
    if (!regionsAttr) return null
    const regions = regionsAttr.regions
    if (!regions || Object.keys(regions).length === 0) return null
    const regionsLabel = getLocalizedValue(regionsAttr.settings.name)

    return (
      <fieldset className="rounded-md border border-white/20 p-2">
        <legend className="px-1 text-xs font-medium text-white/80">{regionsLabel}</legend>
        <div className="flex flex-wrap gap-1.5">
          {Object.values(regions).map((region, index) => (
            <Badge key={index} variant="destructive" className="rounded-sm px-1">
              {getLocalizedValue(region)}
            </Badge>
          ))}
        </div>
      </fieldset>
    )
  }

  const renderPlanDetails = () => (
    <div className="space-y-2 overflow-y-auto">
      <ul className="flex flex-col items-start justify-center gap-y-1.5 text-white/85">
        <li className="flex items-center space-x-1.5 rtl:space-x-reverse">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={cn('size-4', isTezNet ? 'text-white' : 'text-primary')}
            fill="none"
            viewBox="0 0 18 18"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.2"
              d="M8.99998 5.30971v3.69026l3.39162 3.39163M17 9c0 4.4183-3.5817 8-8 8-4.41828 0-8-3.5817-8-8 0-4.41828 3.58172-8 8-8 4.4183 0 8 3.58172 8 8Z"
            />
          </svg>
          <span>{localizedBundle.validity}</span>
        </li>
        {localizedBundle.attributes
          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
          .filter((attribute) => attribute.category !== 'country')
          .map((attribute: any) => (
            <BundleDetailLineItemPlus
              attribute={attribute}
              category={attribute.category}
              key={attribute.id}
              field={attribute.value}
              icon={attribute.settings.icon}
              label={attribute.settings?.name}
              isUnlimited={attribute.is_unlimited}
              iconClassName={isTezNet ? 'text-white' : ''}
            />
          ))}
      </ul>
      <div
        className={cn(
          'flex items-center space-x-1 pt-2 text-white rtl:flex-row-reverse rtl:space-x-reverse',
          isTezNet ? 'absolute inset-x-0 bottom-6 left-6 rtl:left-auto rtl:right-6' : ''
        )}
      >
        <LazySvg name="/icons/currency-alt.svg" className="size-5" />
        <div className="text-3xl font-bold">{bundle.price}</div>
      </div>
      <div
        className={cn(stage === FormStage.PHONE && 'hidden transition-all delay-500 duration-300')}
      >
        {getRegions()}
        {getRates()}
      </div>
    </div>
  )

  const renderExpandedHeader = () => (
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-bold leading-tight" style={{ fontSize: titleFontSize }}>
          {localizedBundle.title}
        </h3>
      </div>
      <Button
        variant="ghost"
        data-expanded={isExpanded}
        className="group absolute right-3 top-3 z-20 size-7 cursor-pointer rounded-xl p-1 hover:bg-secondary/20 rtl:left-3 rtl:right-auto"
        size="icon"
        onClick={(e) => {
          e.stopPropagation()
          handleClose()
        }}
      >
        <X className="size-5 transition-all group-data-[expanded=true]:group-hover:rotate-180 group-data-[expanded=true]:group-hover:text-primary" />
        <span className="sr-only">Close</span>
      </Button>
      {getCountry() && (
        <div className="absolute right-2 top-2 rtl:left-2 rtl:right-auto">
          <img
            src={getCountry() || ''}
            alt={localizedBundle.title}
            className="h-24 w-full rounded-xl bg-repeat-x object-contain opacity-20 blur-[2px]"
          />
        </div>
      )}
    </div>
  )

  const renderForm = () => (
    <AnimatePresence mode="wait">
      {stage !== FormStage.INITIAL && (
        <motion.div
          key={stage}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-grow px-0.5 py-3"
        >
          {stage === FormStage.PHONE ? (
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-300">
                {t('plan.phoneNumber')}
              </Label>
              <Input
                ref={phoneRef}
                id="phone"
                type="tel"
                className="h-12 border-gray-700 bg-gray-800 text-[20px] font-semibold tabular-nums leading-3 tracking-wider text-white focus:border-primary focus-visible:ring-primary rtl:font-sans rtl:tabular-nums rtl:placeholder:text-right rtl:placeholder:font-rtl"
                placeholder={t('plan.phoneNumberPlaceholder')}
                value={phoneForm.data.msisdn || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  phoneForm.setData('msisdn', value)
                  phoneForm.clearErrors('msisdn')
                  setPhoneTouched(false)
                  setHideInlineError(false)
                }}
                onBlur={() => setPhoneTouched(true)}
                disabled={phoneForm.processing}
              />
              {phoneTouched && (phoneError || (phoneForm.data.msisdn && !isValidPhone)) && !rateLimitTimer && !hideInlineError && (
                <p className="mt-2 text-wrap text-sm text-red-400" role="alert">
                  {phoneError || t('validations.phoneNumber.invalid')}
                </p>
              )}
              {rateLimitTimer > 0 && (
                <p className="mt-2 text-wrap text-sm text-red-400" role="alert">
                  <span className="block">{t('validations.rateLimited')}</span>
                  <span className="block tabular-nums">
                    {t('plan.rateLimitCountdown', { time: formatTime(rateLimitTimer) })}
                  </span>
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-center">
                <Label className="block text-gray-300 rtl:text-base">{t('plan.otp.title')}</Label>
                <p className="mt-1 text-sm text-gray-400">
                  {otpForm.data.msisdn || phoneForm.data.msisdn}
                </p>
              </div>
              <div className="flex justify-center" dir="ltr">
                <InputOTP
                  ref={otpRef}
                  autoFocus
                  maxLength={6}
                  value={otpForm.data.pin || ''}
                  onChange={(value) => {
                    otpForm.setData('pin', value)
                    otpForm.clearErrors('pin')
                    setHideInlineError(false)
                  }}
                  disabled={otpForm.processing}
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="!flex h-12 w-12 border-gray-700 bg-gray-800 text-lg text-white focus:border-primary [&_.animate-caret-blink]:bg-white"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpError && !rateLimitTimer && !hideInlineError && (
                <p
                  className="mt-2 flex-grow-0 text-wrap text-center text-sm text-red-400"
                  role="alert"
                >
                  {otpError}
                </p>
              )}
              {rateLimitTimer > 0 && (
                <p
                  className="mt-2 flex-grow-0 text-wrap text-center text-sm text-red-400"
                  role="alert"
                >
                  <span className="block">{t('validations.rateLimited')}</span>
                  <span className="block tabular-nums">
                    {t('plan.rateLimitCountdown', { time: formatTime(rateLimitTimer) })}
                  </span>
                </p>
              )}
              <div className="text-wrap text-center">
                <p className="text-sm text-muted-foreground">
                  {t('plan.otp.resendNote')}{' '}
                  <button
                    type="button"
                    className={cn(
                      'font-medium tabular-nums transition-colors duration-200',
                      canResend
                        ? 'text-primary hover:text-primary/80 hover:underline'
                        : 'cursor-not-allowed text-muted-foreground'
                    )}
                    onClick={() => {
                      if (canResend && !phoneForm.processing) {
                        // Clear OTP input and errors before resending
                        otpForm.setData('pin', '')
                        otpForm.clearErrors()
                        setHideInlineError(false)
                        
                        // Actually resend the OTP via the backend
                        phoneForm.post('/bundles/verify', {
                          onSuccess: () => {
                            setResendTimer(120)
                            setCanResend(false)
                            toast({
                              title: t('plan.informational.otpResendTitle'),
                              description: t('plan.informational.otpResendDescription'),
                              variant: 'success',
                            })
                            setTimeout(() => {
                              otpRef.current?.focus()
                            }, 100)
                          },
                          onError: (errors) => {
                            const retryAfter = errors.retryAfter as string | undefined
                            if (retryAfter) {
                              setRateLimitTimer(parseInt(retryAfter, 10))
                            }
                          },
                          preserveScroll: true,
                        })
                      }
                    }}
                    disabled={phoneForm.processing || !canResend}
                  >
                    {phoneForm.processing && canResend
                      ? t('plan.otp.resending')
                      : canResend
                        ? t('plan.otp.resend')
                        : t('plan.otp.resendMessage', { time: formatTime(resendTimer) })}
                  </button>
                </p>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )

  const renderActionButtons = () => {
    const isProcessing = phoneForm.processing || otpForm.processing
    // Handle monetization logic
    if (bundle?.monetization === 'no') return null

    if (bundle?.monetization === 'myroshan') {
      const getStoreLink = () => {
        const userAgent = navigator.userAgent.toLowerCase()
        const isIOS = /iphone|ipad|ipod|mac/.test(userAgent)

        return isIOS
          ? 'https://apps.apple.com/pk/app/my-roshan/id1512175766'
          : 'https://play.google.com/store/apps/details?id=af.roshan.myroshan'
      }

      return (
        <div className="inline-flex w-full justify-center">
          <Button variant="destructive" asChild>
            <a
              href={getStoreLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rtl:text-base rtl:font-medium"
            >
              {t('plan.activate')}
            </a>
          </Button>
        </div>
      )
    }

    return (
      <div className="inline-flex">
        {stage !== FormStage.INITIAL && (
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={isProcessing}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            {t('plan.back')}
          </Button>
        )}

        {stage === FormStage.INITIAL && (
          <Button
            className="w-full rtl:text-base rtl:font-medium"
            onClick={handleNext}
            disabled={isProcessing}
          >
            {t('plan.activate')}
          </Button>
        )}

        {stage === FormStage.PHONE && (
          <Button
            onClick={handleNext}
            disabled={!canProceedToPin || isProcessing || rateLimitTimer > 0}
            className="ml-auto flex items-center gap-2 rtl:ml-0 rtl:mr-auto"
          >
            {phoneForm.processing ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('plan.nextLoading')}
              </>
            ) : (
              <>
                {t('plan.next')}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </>
            )}
          </Button>
        )}

        {stage === FormStage.PIN && (
          <Button
            onClick={handleActivate}
            disabled={!canActivate || isProcessing || otpForm.hasErrors || rateLimitTimer > 0}
            className="ms-auto min-w-[120px]"
          >
            {otpForm.processing ? (
              <>
                <div className="me-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {t('plan.confirmLoading')}
              </>
            ) : (
              t('plan.confirm')
            )}
          </Button>
        )}
      </div>
    )
  }

  return (
    <motion.div
      className={cn(
        'dynamic-island group overflow-hidden rounded-[22px] border-border/80 shadow-xl dark:border',
        isExpanded
          ? 'dynamic-island fixed inset-0 z-[9999] m-auto h-fit max-h-[90vh] min-h-fit w-[380px] border-border/50 text-white md:inset-24'
          : 'relative h-[190px] w-full cursor-pointer', // Simplified to be fully responsive
        isTezNet ? 'bg-blue-950' : 'bg-black',
        isTezNet && isExpanded ? 'h-[238px]' : ''
      )}
      layout
      ref={modalRef}
      onClick={() => !isExpanded && onClick()}
      whileHover="hover"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        // Universal GPU acceleration for smooth animations
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        perspective: '1000px',
      }}
    >
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          <>
            {getCountry() && (
              <div className="absolute right-2 top-2 z-10 rtl:left-2 rtl:right-auto">
                <img
                  src={getCountry() || ''}
                  alt={localizedBundle.title}
                  className="size-10 rounded-full border border-white/20 object-contain"
                />
              </div>
            )}
            {!isTezNet && (
              <motion.div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${bundle.settings?.image ? bundle.settings.image : '/images/placeholder.svg'})`,
                }}
                variants={imageVariants}
                transition={{ ease: 'easeOut', duration: 0.3 }}
              />
            )}
            {/* Primary blue background */}
            {isTezNet && (
              <div className="absolute inset-0 bg-blue-800/95 dark:bg-blue-900/95">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-700/80 via-blue-800/90 to-blue-900/95" />
                  <div className="absolute -left-10 top-0 h-80 w-80 rounded-full bg-blue-400/30 mix-blend-soft-light blur-3xl rtl:-right-10 rtl:left-auto" />
                  <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-red-500/20 mix-blend-overlay blur-xl rtl:left-0 rtl:right-auto" />
                  <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-red-500/10 mix-blend-multiply blur-2xl rtl:left-0 rtl:right-auto" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/5" />
                </div>
              </div>
            )}
            <div
              className={cn(
                'absolute inset-0 p-4',
                isTezNet ? 'bg-blue-900/10 bg-gradient-to-t' : 'bg-black bg-opacity-50'
              )}
            >
              <div className="flex h-full flex-col justify-between">
                <div>
                  <h3
                    className="pe-8 font-bold leading-none tracking-tight text-white"
                    style={{ fontSize: titleFontSize }}
                  >
                    {localizedBundle.title}
                  </h3>

                  <div className="mb-4 mt-1 flex flex-wrap gap-1">
                    {isTezNet &&
                      localizedBundle.attributes?.map((attribute: any) => (
                        <Badge
                          key={attribute.category}
                          variant="secondary"
                          className="h-5 bg-background/20 text-xs font-medium text-muted transition-opacity hover:bg-background/30 dark:bg-foreground/10 dark:text-foreground/70"
                        >
                          {attribute.value}
                        </Badge>
                      ))}
                    {!isTezNet &&
                      localizedBundle.attributes
                        ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                        .filter((attribute) => attribute.show_as_subtitle)
                        .map((attribute: any) => (
                          <Badge
                            key={attribute.category}
                            variant="secondary"
                            className="inline-flex h-5 space-x-1 bg-gray-950/50 pe-1 ps-1 text-xs font-medium text-white transition-opacity hover:bg-gray-950/50 rtl:space-x-reverse"
                          >
                            {attribute.settings.icon && (
                              <LazySvg
                                name={attribute.settings.icon}
                                containsPath={true}
                                className="size-3 text-primary"
                              />
                            )}
                            <span>{attribute.value}</span>
                            {attribute.is_unlimited && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                className="size-4 text-primary"
                                viewBox="0 0 22 11"
                              >
                                <path
                                  stroke="currentColor"
                                  strokeMiterlimit="10"
                                  strokeWidth="1.6"
                                  d="M2.30939 8.65889c1.7468 1.74681 4.57938 1.74681 6.32618 0l3.16263-3.16261-3.16263-3.16262C6.88877.58686 4.05619.58686 2.30939 2.3327c-1.74585 1.74776-1.74585 4.57939 0 6.32619ZM19.8836 8.1911c1.4885-1.48852 1.4885-3.90209 0-5.38965-1.4885-1.48851-3.9021-1.48851-5.3896 0l-2.6949 2.69483L14.494 8.1911c1.4885 1.48851 3.9021 1.48851 5.3896 0Z"
                                />
                              </svg>
                            )}
                          </Badge>
                        ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center text-white">
                    <LazySvg name="/icons/currency-alt.svg" className="size-5" />
                    <div className="text-2xl font-bold tabular-nums" dir={i18n.dir()}>
                      <span>{bundle.price}</span>
                      <span className="text-sm font-normal rtl:font-medium">
                        / {localizedBundle.validity}
                      </span>
                    </div>
                  </div>
                  <span className="relative flex items-center justify-start rounded-lg bg-primary/85 py-0.5 ps-1.5 text-xs font-bold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-85">
                    {t('bundleCartDetails')}
                    {/* The bounce is a transform of its own, so the turn sits on a wrapper. */}
                    <span className="ms-0.5 flex rtl:rotate-180">
                      <ChevronRight className="h-4 w-4 animate-bounce-x" strokeWidth={3} />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-fit w-full flex-col space-y-2 overflow-hidden p-6">
            {renderExpandedHeader()}
            <div className="min-h-0 flex-1">
              {renderPlanDetails()}
              {renderForm()}
            </div>
            {!isTezNet && renderActionButtons()}
          </div>
        )}
      </AnimatePresence>
      {isHovered && !isExpanded && (
        <BorderBeam
          duration={6}
          size={100}
          className={cn('from-transparent to-transparent', isTezNet ? 'via-white' : 'via-primary')}
        />
      )}
      {isExpanded && (
        <>
          <BorderBeam
            duration={isTezNet ? 12 : 10} // Slower animation for Safari TezNet
            size={200}
            reverse
            className={cn(
              'from-transparent to-transparent',
              isTezNet ? 'via-white' : 'via-primary'
            )}
          />
          <BorderBeam
            duration={10}
            delay={5}
            size={200}
            reverse
            className="from-transparent via-business-blue to-transparent"
          />
        </>
      )}
    </motion.div>
  )
}

export default function TelecomPlansGrid1({
  bundles,
  title,
  subtitle,
  className,
}: TelecomPlansGrid1Props) {
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null)
  const localizedSectionTitle = useLocalizedValue(title || {})
  const localizedSectionSubtitle = useLocalizedValue(subtitle || {})
  const { i18n } = useTranslation()
  const direction = useSafeDirection()

  useEffect(() => {
    document.body.style.overflow = expandedPlan !== null ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [expandedPlan])

  const handleClose = useCallback(() => {
    setExpandedPlan(null)
  }, [])

  return (
    <section
      className={cn(
        'flex flex-col items-center justify-center',
        Boolean(localizedSectionTitle?.trim()) || Boolean(localizedSectionSubtitle?.trim())
          ? 'gap-10'
          : ''
      )}
    >
      <div className="text-center">
        {localizedSectionTitle && (
          <WordFadeIn
            key={localizedSectionTitle}
            className="text-[32px] font-semibold leading-tight tracking-tight text-foreground"
            words={localizedSectionTitle || ''}
            useWhileInView={true}
            viewportOnce={true}
            viewportAmount={1}
            delay={0.3}
          />
        )}
        {localizedSectionSubtitle && (
          <WordFadeIn
            key={localizedSectionSubtitle}
            className="px-10 text-xl font-normal leading-tight tracking-tight text-body"
            words={localizedSectionSubtitle || ''}
            useWhileInView={true}
            viewportOnce={true}
            viewportAmount={1}
            delay={0.05}
          />
        )}
      </div>
      <div
        className={cn(
          'w-full',
          className
            ? className
            : 'grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 lg:gap-4'
        )}
        style={{ direction }}
      >
        {bundles.map((bundle, index) => (
          <motion.div
            initial={index % 2 === 0 ? { rotateY: -90, opacity: 0 } : { rotateX: -90, opacity: 0 }}
            whileInView={index % 2 === 0 ? { rotateY: 0, opacity: 1 } : { rotateX: 0, opacity: 1 }}
            viewport={{ once: true, margin: '100px' }}
            transition={{
              type: 'tween',
              delay: 0.175,
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            key={index}
          >
            <DynamicIsland
              bundle={bundle}
              isExpanded={expandedPlan === index}
              onClick={() => setExpandedPlan(index)}
              onClose={() => {
                setExpandedPlan(null)
              }}
            />
          </motion.div>
        ))}
      </div>
      {expandedPlan !== null && (
        <div
          className="fixed inset-0 z-[9998] bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={(e) => {
            e.preventDefault()
            handleClose()
          }}
          style={{
            backdropFilter: 'blur(4px)',
          }}
        />
      )}
    </section>
  )
}

// "use client"

// import { useEffect, useRef, useState } from "react"
// import { motion, AnimatePresence } from "framer-motion"
// import "./morphing-card.css"

// const GAMES = [
//   {
//     title: "The Oddysey",
//     description: "Explore unknown galaxies.",
//     longDescription:
//       "Throughout their journey, players will encounter diverse alien races, each with their own unique cultures and technologies. Engage in thrilling space combat, negotiate complex diplomatic relations, and make critical decisions that affect the balance of power in the galaxy.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/space.png",
//   },
//   {
//     title: "Angry Rabbits",
//     description: "They are coming for you.",
//     longDescription:
//       "The rabbits are angry and they are coming for you. You have to defend yourself with your carrot gun. The game is not simple, you have to be fast and accurate to survive.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/rabbit.png",
//   },
//   {
//     title: "Ghost town",
//     description: "Find the ghosts.",
//     longDescription: "You are in a ghost town and you have to find the ghosts. But be careful, they are dangerous.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/ghost.webp",
//   },
//   {
//     title: "Pirates in the jungle",
//     description: "Find the treasure.",
//     longDescription:
//       "You are a pirate and you have to find the treasure in the jungle. But be careful, there are traps and wild animals.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/pirate.png",
//   },
//   {
//     title: "Lost in the mountains",
//     description: "Find your way home.",
//     longDescription:
//       "You are lost in the mountains and you have to find your way home. But be careful, there are dangerous animals and you can get lost.",
//     image:
//       "https://animations-on-the-web-git-how-i-use-3066e1-emilkowalski-s-team.vercel.app/how-i-use-framer-motion/how-i-code-animations/boy.webp",
//   },
// ]

// export default function MorphingCard() {
//   const [activeGame, setActiveGame] = useState(null)
//   const modalRef = useRef(null)

//   // Handle escape key press
//   useEffect(() => {
//     function onKeyDown(event) {
//       if (event.key === "Escape") {
//         setActiveGame(null)
//       }
//     }

//     window.addEventListener("keydown", onKeyDown)
//     return () => window.removeEventListener("keydown", onKeyDown)
//   }, [])

//   // Handle click outside
//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (modalRef.current && !modalRef.current.contains(event.target)) {
//         setActiveGame(null)
//       }
//     }

//     if (activeGame) {
//       document.addEventListener("mousedown", handleClickOutside)
//     }

//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside)
//     }
//   }, [activeGame])

//   return (
//     <>
//       <AnimatePresence>
//         {activeGame ? (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 bg-black/50 backdrop-blur-[1px] z-40"
//           />
//         ) : null}
//       </AnimatePresence>
//       <AnimatePresence>
//         {activeGame ? (
//           <div className="active-game">
//             <motion.div
//               ref={modalRef}
//               layoutId={`card-${activeGame.title}`}
//               className="inner"
//               style={{ borderRadius: 12 }}
//             >
//               <div className="flex gap-4">
//                 <motion.img
//                   layoutId={`image-${activeGame.title}`}
//                   height={56}
//                   width={56}
//                   alt="Game"
//                   src={activeGame.image}
//                   style={{ borderRadius: 12 }}
//                 />
//                 <div className="flex items-center justify-between flex-grow">
//                   <div className="flex flex-col py-4">
//                     <motion.h2 layoutId={`title-${activeGame.title}`} className="m-0 text-base font-semibold">
//                       {activeGame.title}
//                     </motion.h2>
//                     <motion.p layoutId={`description-${activeGame.title}`} className="mt-1 mb-0 text-sm text-gray-600">
//                       {activeGame.description}
//                     </motion.p>
//                   </div>
//                   <motion.button
//                     layoutId={`button-${activeGame.title}`}
//                     className="bg-gray-100 border-none rounded-full px-4 py-1.5 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gray-200"
//                   >
//                     Get
//                   </motion.button>
//                 </div>
//               </div>
//               <motion.p
//                 layout
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 exit={{ opacity: 0, transition: { duration: 0.05 } }}
//                 className="mt-6 text-sm leading-relaxed text-gray-700"
//               >
//                 {activeGame.longDescription}
//               </motion.p>
//             </motion.div>
//           </div>
//         ) : null}
//       </AnimatePresence>

//       <ul className="relative flex flex-col items-center w-full p-0 my-12">
//         {GAMES.map((game, index) => (
//           <motion.li
//             layoutId={`card-${game.title}`}
//             key={game.title}
//             onClick={() => setActiveGame(game)}
//             style={{ borderRadius: 8 }}
//             className="flex w-[386px] cursor-pointer items-center gap-4 p-0"
//           >
//             <motion.img
//               layoutId={`image-${game.title}`}
//               height={56}
//               width={56}
//               alt="Game"
//               src={game.image}
//               style={{ borderRadius: 12 }}
//             />
//             <div
//               className={`flex flex-grow items-center justify-between ${
//                 index === GAMES.length - 1 ? "" : "border-b border-gray-200/40"
//               }`}
//             >
//               <div className="flex flex-col py-4">
//                 <motion.h2 layoutId={`title-${game.title}`} className="m-0 text-base font-semibold">
//                   {game.title}
//                 </motion.h2>
//                 <motion.p layoutId={`description-${game.title}`} className="mt-1 mb-0 text-sm text-gray-600">
//                   {game.description}
//                 </motion.p>
//               </div>
//               <motion.button
//                 layoutId={`button-${game.title}`}
//                 className="bg-gray-100 border-none rounded-full px-4 py-1.5 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gray-200"
//               >
//                 Get
//               </motion.button>
//             </div>
//           </motion.li>
//         ))}
//       </ul>
//     </>
//   )
// }
/* Active-game class */
// .active-game {
//     position: fixed;
//     inset: 0;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     z-index: 50;
//     padding: 16px;
//   }

//   /* Inner class */
//   .inner {
//     background: white;
//     width: 100%;
//     max-width: 500px;
//     padding: 24px;
//     box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
//   }
